import type {
  MarketplaceDetailEventProps, MarketplaceDetailSurface,
  MarketplaceItemEventProps, MarketplaceCompareEventProps,
  MarketplaceProviderClickEventProps, MarketplaceProviderClickSurface,
} from "@/lib/analytics";
import type { ExperienceItem } from "@/lib/types";
import type { MarketplaceItem } from "./marketplaceInventory";

export function buildDetailEventProps(
  item: MarketplaceItem,
  cityIata: string | null | undefined,
  surface: MarketplaceDetailSurface,
): MarketplaceDetailEventProps {
  return {
    experience_id: item.exp.id,
    iata: cityIata ?? undefined,
    source_type: item.exp.source_type ?? null,
    provider_name: item.exp.provider_name ?? null,
    marketplace_relevance_class: item.exp.marketplace_relevance_class ?? null,
    surface,
  };
}

// ─── Launch Analytics & Funnel Instrumentation V1 ──────────────────────────
// Pure builders for the new marketplace_detail_opened/compare_item_added/
// provider_clicked payloads — never a title, description, or booking_url;
// provider_name/category are read as-is from the backend's own fields, not
// inferred. `has_price`/`has_rating`/`has_booking_url` are booleans so a
// dashboard can measure "does the detail view had real signals to show"
// without ever seeing the underlying values.

export function buildMarketplaceItemEventProps(
  exp: ExperienceItem,
  cityIata: string | null | undefined,
): MarketplaceItemEventProps {
  const isProvider = exp.source_type === "provider";
  return {
    destination_iata: cityIata ?? undefined,
    provider_name:    isProvider ? (exp.provider_name ?? null) : null,
    category:         exp.category ?? null,
    has_price:        exp.price_from != null,
    has_rating:       typeof exp.rating === "number" && exp.rating > 0,
    has_booking_url:  typeof exp.booking_url === "string" && exp.booking_url.length > 0,
  };
}

export function buildCompareItemAddedProps(
  exp: ExperienceItem,
  cityIata: string | null | undefined,
  comparisonSize: number,
): MarketplaceCompareEventProps {
  const isProvider = exp.source_type === "provider";
  return {
    destination_iata: cityIata ?? undefined,
    provider_name:    isProvider ? (exp.provider_name ?? null) : null,
    category:         exp.category ?? null,
    comparison_size:  comparisonSize,
  };
}

export function buildProviderClickedProps(
  exp: ExperienceItem,
  cityIata: string | null | undefined,
  surface: MarketplaceProviderClickSurface,
): MarketplaceProviderClickEventProps {
  const isProvider = exp.source_type === "provider";
  return {
    destination_iata: cityIata ?? undefined,
    provider_name:    isProvider ? (exp.provider_name ?? null) : null,
    category:         exp.category ?? null,
    surface,
    has_price:        exp.price_from != null,
    has_rating:       typeof exp.rating === "number" && exp.rating > 0,
    has_booking_url:  typeof exp.booking_url === "string" && exp.booking_url.length > 0,
    // Provider Landing Accuracy + Query Expansion Prep V1 — every surface
    // renders href={exp.booking_url} verbatim (see providerLinkState.ts),
    // the same URL the backend already decorated with affiliate params —
    // never a second, locally-built variant.
    link_variant:     "current_affiliate",
  };
}
